import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, FileText, Building2, Calendar } from "lucide-react";
import { LogoMark } from "@/components/brand/Logo";

const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

const STATUS_TONE = {
  draft:    { label: "Draft",    cls: "text-slate-400 border-slate-700" },
  sent:     { label: "Awaiting approval", cls: "text-sky-300 border-sky-500/40 bg-sky-500/10" },
  accepted: { label: "Approved", cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
  rejected: { label: "Rejected", cls: "text-rose-300 border-rose-500/40 bg-rose-500/10" },
  expired:  { label: "Expired",  cls: "text-slate-400 border-slate-700" },
};

export default function PublicQuote() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/quotes/${token}`);
      setData(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    if (!name.trim()) { toast.error("Please type your full name"); return; }
    setBusy("approve");
    try {
      await api.post(`/public/quotes/${token}/approve`, { signed_name: name.trim() });
      toast.success("Quote approved. Thank you!");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(null); }
  };
  const reject = async () => {
    setBusy("reject");
    try {
      await api.post(`/public/quotes/${token}/reject`, { reason });
      toast.info("Response recorded.");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="min-h-screen bg-brand-bg grid place-items-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (error || !data) return <div className="min-h-screen bg-brand-bg grid place-items-center text-slate-400 text-sm">Quote not found or link expired.</div>;

  const { quote: q, brand, client } = data;
  const status = STATUS_TONE[q.status] || STATUS_TONE.draft;
  const readOnly = q.status === "accepted" || q.status === "rejected" || q.status === "expired";

  return (
    <div className="min-h-screen bg-brand-bg noise" data-testid="public-quote">
      <div className="max-w-4xl mx-auto p-4 sm:p-8">
        {/* Company brand header */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6 sm:p-8 animate-fade-in-up">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              {brand?.logo ? (
                <img src={brand.logo} alt={brand.name} className="w-14 h-14 rounded-lg object-cover border border-slate-800" />
              ) : (
                <LogoMark size={54} withGlow />
              )}
              <div>
                <div className="text-lg font-display font-semibold text-white">{brand?.name || "Yosi Media Studio"}</div>
                {brand?.email && <div className="text-xs text-slate-400">{brand.email}</div>}
                {brand?.phone && <div className="text-xs text-slate-400">{brand.phone}</div>}
                {brand?.website && <div className="text-xs text-slate-400">{brand.website}</div>}
              </div>
            </div>
            <div className="text-right">
              <div className="font-display text-3xl font-semibold text-white tracking-tight">QUOTE</div>
              <div className="mt-1 text-xs text-slate-400 font-mono">{q.reference || q.quote_id}</div>
              <div className={`mt-2 inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] uppercase tracking-widest ${status.cls}`} data-testid="public-status">{status.label}</div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
            <div>
              <div className="overline mb-1">Bill to</div>
              {client ? (
                <>
                  <div className="text-white font-medium">{client.name}</div>
                  {client.company && <div className="text-slate-400">{client.company}</div>}
                  {client.email && <div className="text-slate-400">{client.email}</div>}
                  {client.phone && <div className="text-slate-400">{client.phone}</div>}
                </>
              ) : <div className="text-slate-500">—</div>}
            </div>
            <div className="sm:text-right">
              <div className="overline mb-1">Subject</div>
              <div className="text-white font-medium">{q.title}</div>
              {q.valid_until && <div className="mt-2 inline-flex items-center gap-1 text-xs text-slate-400"><Calendar className="w-3 h-3" /> Valid until {q.valid_until}</div>}
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className="mt-6 rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden animate-fade-in-up delay-1" data-testid="public-items">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-400 bg-slate-900/80">
                <th className="p-3">#</th><th className="p-3">Description</th><th className="p-3 text-right">Qty</th><th className="p-3 text-right">Unit price</th><th className="p-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(q.items || []).map((it, i) => (
                <tr key={`row-${i}-${it.description?.slice(0, 12) || ""}`} className="border-t border-slate-800">
                  <td className="p-3 text-slate-500">{i + 1}</td>
                  <td className="p-3 text-white">{it.description}</td>
                  <td className="p-3 text-slate-300 text-right">{Number(it.quantity || 0)}</td>
                  <td className="p-3 text-slate-300 text-right">{money(it.unit_price)}</td>
                  <td className="p-3 text-white text-right font-medium">{money(Number(it.quantity || 0) * Number(it.unit_price || 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6 animate-fade-in-up delay-2">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6 text-sm">
            {q.notes && (<><div className="overline mb-1">Notes</div><div className="text-slate-300 whitespace-pre-wrap">{q.notes}</div></>)}
            {q.terms && (<div className={q.notes ? "mt-4" : ""}><div className="overline mb-1">Terms &amp; conditions</div><div className="text-slate-400 text-xs whitespace-pre-wrap">{q.terms}</div></div>)}
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6 text-sm">
            <div className="flex justify-between py-1"><span className="text-slate-400">Subtotal</span><span className="text-white">{money(q.subtotal)}</span></div>
            {Number(q.discount || 0) > 0 && <div className="flex justify-between py-1"><span className="text-slate-400">Discount</span><span className="text-rose-300">-{money(q.discount)}</span></div>}
            {Number(q.tax || 0) > 0 && <div className="flex justify-between py-1"><span className="text-slate-400">Tax ({q.tax_rate || 0}%)</span><span className="text-white">{money(q.tax)}</span></div>}
            <div className="flex justify-between py-3 border-t border-slate-800 mt-2 items-baseline"><span className="text-white font-medium">Total</span><span className="text-blue-400 font-display text-2xl font-semibold" data-testid="public-total">{money(q.total)}</span></div>
          </div>
        </div>

        {/* Approval */}
        <div className="mt-6 rounded-2xl border border-slate-800 p-6 animate-fade-in-up delay-3" data-testid="public-approval" style={{ background: "linear-gradient(135deg, rgba(37,99,235,0.08), rgba(56,189,248,0.05))" }}>
          {q.status === "accepted" ? (
            <div className="flex items-center gap-3 text-emerald-300">
              <CheckCircle2 className="w-6 h-6" />
              <div>
                <div className="text-white font-medium">Approved by {q.approval?.signed_name}</div>
                <div className="text-xs text-slate-400">on {(q.approval?.at || "").slice(0, 19).replace("T", " ")} UTC · IP {q.approval?.ip}</div>
              </div>
            </div>
          ) : q.status === "rejected" ? (
            <div className="flex items-center gap-3 text-rose-300">
              <XCircle className="w-6 h-6" />
              <div>
                <div className="text-white font-medium">Quote declined</div>
                {q.rejection?.reason && <div className="text-xs text-slate-400 mt-1">Reason: {q.rejection.reason}</div>}
              </div>
            </div>
          ) : (
            <>
              <div className="text-white font-display font-semibold text-lg">Electronic approval</div>
              <p className="mt-1 text-sm text-slate-400">Type your full name to accept this quote. Your signature, timestamp and IP address will be recorded for our records.</p>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input data-testid="approve-name" placeholder="Full legal name" value={name} onChange={(e) => setName(e.target.value)} className="h-11 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm text-white" />
                <button data-testid="approve-btn" onClick={approve} disabled={busy === "approve"} className="btn-scale h-11 rounded-md font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-70" style={{ backgroundImage: "linear-gradient(135deg, #2563EB, #38BDF8)" }}>
                  {busy === "approve" && <Loader2 className="w-4 h-4 animate-spin" />}
                  <CheckCircle2 className="w-4 h-4" /> I approve this quote
                </button>
              </div>
              <div className="mt-4 border-t border-slate-800 pt-4">
                <details>
                  <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-200">I need to decline this quote</summary>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input data-testid="reject-reason" placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} className="h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm" />
                    <button data-testid="reject-btn" onClick={reject} disabled={busy === "reject"} className="btn-scale h-10 rounded-md border border-slate-700 text-slate-200 hover:bg-slate-800 text-sm flex items-center justify-center gap-2 disabled:opacity-70">
                      {busy === "reject" && <Loader2 className="w-4 h-4 animate-spin" />}
                      <XCircle className="w-4 h-4" /> Decline
                    </button>
                  </div>
                </details>
              </div>
            </>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between text-[11px] text-slate-500">
          <div className="flex items-center gap-2">
            <LogoMark size={18} />
            <span>{brand?.name || "Yosi Media Studio"} · Yosi Media Studio</span>
          </div>
          <div>Reference: <span className="font-mono">{q.reference || q.quote_id}</span></div>
        </div>
      </div>
    </div>
  );
}
